import { zodResolver } from '@hookform/resolvers/zod'
import type { ClientDevice } from '@maru44/huntre-utils/src/models/clientDevice'
import type { ClientMember } from '@maru44/huntre-utils/src/models/clientMember'
import type { ClientSubGroup, ClientSubGroupInput } from '@maru44/huntre-utils/src/models/clientSubGroup'
import { Autocomplete, Box, Button, Chip, InputLabel, Stack, TextField } from '@mui/material'
import { type FormEvent, useCallback, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { DomainError } from 'src/utils/error'
import { capture } from 'src/utils/sentry'
import { z } from 'zod'

type Props = {
  initialValue?: ClientSubGroup
  clientDevices: ClientDevice[]
  clientMembers: ClientMember[]
  warningMessage?: string
  submitLabel: string
  onSubmit: (v: ClientSubGroupInput) => Promise<void>
  onCancel?: () => void
}

const schema = z.object({
  name: z.string().min(1, '名前を入力してください').max(254, '名前が長すぎます'),
  clientDeviceIds: z.array(z.string()),
  clientMemberIds: z.array(z.string()),
})
type FormState = z.infer<typeof schema>

const defaultValues: FormState = {
  name: '',
  clientDeviceIds: [],
  clientMemberIds: [],
}

export const ClientSubGroupForm = ({ initialValue, clientDevices, clientMembers, warningMessage, submitLabel, onSubmit, onCancel }: Props) => {
  const { control, reset, handleSubmit, formState } = useForm<FormState>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'all',
  })

  useEffect(() => {
    reset(
      initialValue
        ? {
            name: initialValue.name,
            clientDeviceIds: initialValue.clientDeviceIds,
            clientMemberIds: initialValue.clientMemberIds,
          }
        : defaultValues,
    )
  }, [initialValue, reset])

  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      try {
        await handleSubmit(async (v) => {
          await onSubmit({
            name: v.name,
            clientDeviceIds: v.clientDeviceIds,
            clientMemberIds: v.clientMemberIds,
            lineTarget: initialValue?.lineTarget ?? null,
          })
        })()
        toast.success('保存しました')
      } catch (err) {
        capture(err)
        toast.error(err instanceof DomainError ? err.message : '保存に失敗しました')
      }
    },
    [onSubmit, handleSubmit, initialValue],
  )

  return (
    <Box component="form" onSubmit={submit}>
      {warningMessage && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: 'warning.light',
            borderRadius: 1,
          }}
        >
          {warningMessage}
        </Box>
      )}
      <Stack spacing={3}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Box>
              <InputLabel>名前</InputLabel>
              <TextField fullWidth {...field} error={!!formState.errors?.name} helperText={formState.errors?.name?.message} />
            </Box>
          )}
        />
        <Controller
          name="clientDeviceIds"
          control={control}
          render={({ field }) => (
            <Box>
              <InputLabel>対象デバイス</InputLabel>
              <Autocomplete
                multiple
                options={clientDevices.map((d) => d.deviceId)}
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                renderValue={(value, getItemProps) =>
                  value.map((option, index) => <Chip variant="outlined" label={option} {...getItemProps({ index })} key={option} />)
                }
                renderInput={(params) => <TextField {...params} name="clientDeviceIds" placeholder="デバイスを選択" />}
              />
            </Box>
          )}
        />
        <Controller
          name="clientMemberIds"
          control={control}
          render={({ field }) => (
            <Box>
              <InputLabel>通知先メンバー</InputLabel>
              <Autocomplete
                multiple
                options={clientMembers.map((m) => m.id)}
                getOptionLabel={(id) => clientMembers.find((m) => m.id === id)?.name ?? id}
                value={field.value}
                onChange={(_, v) => field.onChange(v)}
                renderValue={(value, getItemProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={clientMembers.find((m) => m.id === option)?.name ?? option} {...getItemProps({ index })} key={option} />
                  ))
                }
                renderInput={(params) => <TextField {...params} name="clientMemberIds" placeholder="メンバーを選択" />}
              />
            </Box>
          )}
        />
      </Stack>
      <Box
        sx={{
          mt: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {onCancel ? (
          <Button variant="outlined" onClick={onCancel}>
            キャンセル
          </Button>
        ) : (
          <span />
        )}
        <Button variant="contained" type="submit" disabled={!formState.isValid || formState.isSubmitting}>
          {submitLabel}
        </Button>
      </Box>
    </Box>
  )
}
